import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMemo } from "react";
import Alert from "../../../components/common/Alert";
import Badge from "../../../components/common/Badge";
import Button from "../../../components/common/Button";
import Card from "../../../components/common/Card";
import Section from "../../../components/common/Section";
import { useGetUsersQuery } from "../../../store/api";
import {
  formatAmountWithImpact,
  formatStatusLabel,
  getStatusTone,
} from "../../../utils/cashingUpReporting";

const DetailPair = ({ label, value, emphasize = false }) => (
  <div className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] items-start gap-4 border-b border-slate-100 py-2.5 last:border-b-0">
    <p className="min-w-0 text-xs font-medium uppercase tracking-wide text-slate-500">
      {label}
    </p>
    <p
      className={[
        "min-w-0 break-words text-slate-900",
        emphasize ? "text-base font-semibold" : "text-sm font-medium",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {value || "-"}
    </p>
  </div>
);

const PaidOutDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const movement = location.state?.movement || null;
  const { data: users } = useGetUsersQuery();
  const title = "Movement";
  const amountValue = Number(movement?.amount || 0);
  const directionValue =
    ["PAID_OUT", "CASH_DROP", "TIP_PAYOUT"].includes(
      String(movement?.movementType || "").toUpperCase(),
    )
      ? -Math.abs(amountValue)
      : amountValue;
  const amountStyle = formatAmountWithImpact(directionValue, movement?.currencyCode);
  const userNameById = useMemo(() => {
    const map = new Map();
    (Array.isArray(users) ? users : []).forEach((user) => {
      const id = user.userId || user.id;
      const name = user.displayName || user.name || user.fullName || user.email || "";
      if (id && name) {
        map.set(id, name);
      }
    });
    return map;
  }, [users]);

  const createdByLabel =
    movement?.createdByUserName ||
    movement?.createdByName ||
    movement?.createdByDisplayName ||
    userNameById.get(movement?.createdByUserId) ||
    userNameById.get(movement?.createdBy) ||
    "-";

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">
            Paid Out {title ? `- ${title}` : ""}
          </h1>
          <p className="text-sm text-slate-600">
            Read-only cash movement detail mirrored into Central.
          </p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          onClick={() =>
            navigate({
              pathname: "/reporting/cashing-up/paid-outs",
              search: location.search,
            })
          }
        >
          Back to Paid Outs
        </Button>
      </div>

      {!movement ? (
        <Alert tone="warning">
          Movement details are only available when you open this page from the
          Paid Outs report.
        </Alert>
      ) : (
        <Card className="p-5 sm:p-6">
          <Section
            title="Movement Summary"
            subtitle="Cash movement information mirrored from the operational side."
          >
            <div className="grid gap-x-8 md:grid-cols-2">
              <DetailPair label="Movement Type" value={movement.movementType || "-"} />
              <DetailPair label="Reason Code" value={movement.reasonCode || "-"} />
              <DetailPair
                label="Status"
                value={
                  <Badge tone={getStatusTone(movement.status)}>
                    {formatStatusLabel(movement.status)}
                  </Badge>
                }
              />
              <DetailPair label="Created By" value={createdByLabel} />
              <DetailPair
                label="Amount"
                value={
                  <span
                    className={
                      amountStyle.tone === "rose"
                        ? "font-semibold text-rose-700"
                        : amountStyle.tone === "emerald"
                          ? "font-semibold text-emerald-700"
                          : "font-semibold text-slate-700"
                    }
                  >
                    {amountStyle.value}
                  </span>
                }
                emphasize
              />
            </div>
          </Section>
        </Card>
      )}
    </div>
  );
};

export default PaidOutDetail;
